import React from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'

const SEO = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  canonical,
  structuredData,
  breadcrumbs
}) => {
  const location = useLocation()
  const siteUrl = 'https://kdgeneraltransport.com'
  const fullUrl = canonical || `${siteUrl}${location.pathname}`
  const defaultImage = `${siteUrl}/img/logo.png`
  const imageUrl = image || defaultImage

  // Enhanced structured data for LocalBusiness with Dubai focus
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": siteUrl,
    "name": "KD General Transport LLC",
    "alternateName": "KD General Transport",
    "image": [
      defaultImage,
      `${siteUrl}/img/cars/freezer2.jpeg`,
      `${siteUrl}/img/cars/chiller2.jpeg`,
      `${siteUrl}/img/cars/mover2.jpeg`,
      `${siteUrl}/img/cars/trailer1.jpeg`
    ],
    "logo": defaultImage,
    "url": siteUrl,
    "telephone": "+971555592206",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Oby Property M.B25 Mussafah 15",
      "addressLocality": "Abu Dhabi",
      "addressRegion": "Abu Dhabi",
      "postalCode": "",
      "addressCountry": "AE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 24.4539,
      "longitude": 54.3773
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      }
    ],
    "sameAs": [
      "https://wa.me/971555592206"
    ],
    "areaServed": [
      {
        "@type": "City",
        "name": "Dubai",
        "@id": "https://www.wikidata.org/wiki/Q613"
      },
      {
        "@type": "City",
        "name": "Abu Dhabi",
        "@id": "https://www.wikidata.org/wiki/Q878"
      },
      {
        "@type": "City",
        "name": "Sharjah",
        "@id": "https://www.wikidata.org/wiki/Q18881"
      },
      {
        "@type": "City",
        "name": "Ajman"
      },
      {
        "@type": "City",
        "name": "Ras Al Khaimah"
      },
      {
        "@type": "City",
        "name": "Fujairah"
      },
      {
        "@type": "City",
        "name": "Umm Al Quwain"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Transportation Services in Dubai, UAE",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Freezer Transportation Dubai",
            "description": "Professional freezer van services for frozen goods transportation in Dubai, UAE. Serving all areas including Downtown Dubai, JBR, Business Bay, and across all 7 Emirates.",
            "areaServed": {
              "@type": "City",
              "name": "Dubai"
            },
            "serviceType": "Freezer Transportation",
            "provider": {
              "@type": "LocalBusiness",
              "name": "KD General Transport LLC"
            }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Chiller Transportation Dubai",
            "description": "Temperature-controlled chiller truck services for perishable goods in Dubai, UAE. Real-time monitoring and 24/7 service across Dubai and all Emirates.",
            "areaServed": {
              "@type": "City",
              "name": "Dubai"
            },
            "serviceType": "Chiller Transportation",
            "provider": {
              "@type": "LocalBusiness",
              "name": "KD General Transport LLC"
            }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Mover Transportation Dubai",
            "description": "Professional moving and relocation services in Dubai, UAE. Home moving, office relocation, and commercial moving across Dubai, Abu Dhabi, Sharjah and all Emirates.",
            "areaServed": {
              "@type": "City",
              "name": "Dubai"
            },
            "serviceType": "Moving Services",
            "provider": {
              "@type": "LocalBusiness",
              "name": "KD General Transport LLC"
            }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Trailer Transportation Dubai",
            "description": "Heavy-duty trailer services including cotton box and flat bed trailers in Dubai, UAE. Large-scale cargo transport across Dubai and all UAE Emirates.",
            "areaServed": {
              "@type": "City",
              "name": "Dubai"
            },
            "serviceType": "Trailer Transportation",
            "provider": {
              "@type": "LocalBusiness",
              "name": "KD General Transport LLC"
            }
          }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127",
      "bestRating": "5",
      "worstRating": "1"
    }
  }

  const finalStructuredData = structuredData || defaultStructuredData

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="KD General Transport LLC" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="geo.region" content="AE" />
      <meta name="geo.placename" content="United Arab Emirates" />
      <meta name="geo.position" content="25.2048;55.2708" />
      <meta name="ICBM" content="25.2048, 55.2708" />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content="KD General Transport LLC" />
      <meta property="og:locale" content="en_AE" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Structured Data - Multiple schemas for better SEO */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
      
      {/* Organization Schema for better brand recognition */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "KD General Transport LLC",
          "url": siteUrl,
          "logo": defaultImage,
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+971555592206",
            "contactType": "Customer Service",
            "areaServed": "AE",
            "availableLanguage": ["English", "Arabic"]
          },
          "sameAs": [
            "https://wa.me/971555592206"
          ]
        })}
      </script>
      
      {/* Website Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "KD General Transport LLC",
          "url": siteUrl,
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${siteUrl}/?s={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
      
      {/* Breadcrumb Schema */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((crumb, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": crumb.label,
              "item": crumb.path && !crumb.path.startsWith('#') ? `${siteUrl}${crumb.path}` : undefined
            }))
          })}
        </script>
      )}
    </Helmet>
  )
}

export default SEO

